import React, { useRef, useEffect, useState } from "react";
import "./PopupMessage.scss"; // Ensure this file contains the custom styles
import ConfirmPopup from "./ConfirmPopup";
import {
	sendToAllTelegram,
	sendToUserTelegram,
	getSMSCounts,
} from "../api/apiServiceFetch";
import resizeImage from "./ResizeImage"; // Import the resizeImage function
import Loading from "./Loading"; // Import the Loading component

const PopupMessage = ({ isOpen, onClose, userId, type }) => {
	const [message, setMessage] = useState("");
	const [media, setMedia] = useState(null);
	const [mediaType, setMediaType] = useState(null);
	const [showPopup, setShowPopup] = useState(false);
	const [loading, setLoading] = useState(false);
	const [counts, setCounts] = useState(null);
	const [openCounts, setOpenCounts] = useState(false);
	const textareaRef = useRef(null);

	const autoResizeTextarea = () => {
		if (textareaRef.current) {
			textareaRef.current.style.height = "auto";
			textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
		}
	};

	useEffect(() => {
		autoResizeTextarea();
	}, [message]);

	// Effect to handle auto-refresh after sending

	const readFileAsBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => resolve(reader.result.split(",")[1]); // Extract base64 part
			reader.onerror = reject;
			reader.readAsDataURL(file);
		});
	};

	const handleConfirm = async () => {
		setShowPopup(false);
		setLoading(true); // Start loading

		let base64Media = null;
		if (media) {
			try {
				if (mediaType === "photo") {
					const wantedWidth = 800;
					const wantedHeight = 800;
					const { base64String } = await resizeImage(
						media,
						wantedWidth,
						wantedHeight
					);
					base64Media = base64String;
				} else if (mediaType === "video") {
					base64Media = await readFileAsBase64(media);
				}
			} catch (error) {
				console.error("Error processing media: ", error);
			}
		}

		const data = {
			userId: userId,
			message: message,
			base64Media: base64Media,
			mediaType: mediaType,
		};

		// Send the data after image is resized and get the counts response
		try {
			if (type === 1) {
				await sendToUserTelegram(data);
			} else {
				data.userId = 0;
				await sendToAllTelegram(data);
			}
		} catch (error) {
			console.error("Error sending message:", error);
		} finally {
			setLoading(false); // Stop loading
			setMessage("");
			setMedia(null);
			setMediaType(null);
			if (type === 1) {
				onCloseHandle();
			} else {
				setOpenCounts(true);
				onRefreshHandle();
			}
		}
	};

	const fetchCounts = async () => {
		try {
			const countsResponse = await getSMSCounts(type);
			setCounts(countsResponse);

			// Check if all messages are processed
			const total = countsResponse?.userCount || 0;
			const processed =
				(countsResponse?.successCount || 0) +
				(countsResponse?.failedCount || 0) +
				(countsResponse?.deletedCount || 0);
			if (processed >= total) {
				setOpenCounts(true); // Ensure counts remain visible
			}
		} catch (error) {
			console.error("Error fetching counts:", error);
		}
	};

	const handleCancel = () => {
		setShowPopup(false);
		setMessage("");
		setMedia(null);
		setMediaType(null);
	};

	const handleSave = () => {
		setShowPopup(true);
	};

	const handleInputChange = (event) => {
		setMessage(event.target.value);
	};

	const handleMediaChange = (event) => {
		const file = event.target.files[0];
		if (file) {
			if (file.type.startsWith("image/")) {
				setMediaType("photo");
				setMedia(file);
			} else if (file.type.startsWith("video/")) {
				setMediaType("video");
				setMedia(file);
			} else {
				console.error("Unsupported file type");
			}
		} else {
			setMedia(null);
			setMediaType(null);
		}
	};

	const onCloseHandle = () => {
		setShowPopup(false);
		setCounts(null);
		onClose();
	};

	const onRefreshHandle = () => {
		fetchCounts();
	};

	if (!isOpen) return null;

	// Calculate progress percentage
	const total = counts?.userCount || 0;
	const processed =
		(counts?.successCount || 0) +
		(counts?.failedCount || 0) +
		(counts?.deletedCount || 0);
	const progress = total > 0 ? Math.round((processed / total) * 100) : 0;

	return (
		<div className="PopupMessage-overlay">
			<ConfirmPopup
				isOpen={showPopup}
				onConfirm={handleConfirm}
				onCancel={handleCancel}
				question={"Вы уверены?"}
			/>
			<div className="PopupMessage-popup">
				<h2>Отправка сообщения</h2>
				{loading && <Loading />}{" "}
				{/* Loading bar displayed when sending messages */}
				{type !== 1 && (
					<div className="counts">
						<table>
							<thead>
								<tr>
									<th>Количество</th>
									<th>Успешно</th>
									<th>Неудачно</th>
									<th>Удалено</th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td>{counts?.userCount || 0}</td>
									<td>{counts?.successCount || 0}</td>
									<td>{counts?.failedCount || 0}</td>
									<td>{counts?.deletedCount || 0}</td>
								</tr>
							</tbody>
						</table>
						<p>
							Дата:{" "}
							{counts?.cDate
								? new Date(counts.cDate)
										.toLocaleString("ru-RU", {
											year: "numeric",
											month: "2-digit",
											day: "2-digit",
											hour: "2-digit",
											minute: "2-digit",
										})
										.replace(",", "")
								: ""}
						</p>
						{/* Progress Bar */}
						{openCounts && (
							<div className="progress-container">
								<div className="progress-bar" style={{ width: `${progress}%` }}>
									{progress}%
								</div>
							</div>
						)}
						<button onClick={onRefreshHandle} className="refresh-button">
							ОБНОВИТЬ
						</button>
					</div>
				)}
				{!loading && (
					<>
						<div>
							<label htmlFor="message">Напишите сообщение</label>
							<textarea
								id="message"
								className={"textArea"}
								name="message"
								value={message}
								onChange={handleInputChange}
								ref={textareaRef}
							/>
						</div>
						<div>
							<label htmlFor="media">Загрузить изображение или видео</label>
							<input
								type="file"
								id="media"
								name="media"
								accept="image/*,video/*"
								onChange={handleMediaChange}
							/>
						</div>
						<div className="popup-buttons">
							<button onClick={handleSave} className="save-button">
								Отправить
							</button>
							<button onClick={onCloseHandle} className="cancel-button">
								Отмена
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	);
};

export default PopupMessage;
